import { Injectable } from '@angular/core';
import Papa, { ParseResult } from 'papaparse';
import { BehaviorSubject, from, iif, Observable, of, Subscriber } from 'rxjs';
import { read, utils, WorkBook, WorkSheet } from 'xlsx';
import { ProjectApiService } from './project-api.service';
import { Project } from '../objects/project';
import { ProjectService } from './project.service';
import { emptyLoadingSpace } from '../../main-panel/add-loading-space/add-loading-space.component';
import { concatMap, switchMap } from 'rxjs/operators';
import { CargoSpace, Group, GroupAdd, Load, ProjectInfo, WorkSpace } from '../types';
import { DatePipe } from '@angular/common';
import { ColorsService } from './colors.service';
import {
  ConvertedLoadGroup,
  CurrentWorkSpace,
  LoadGroup,
  LoadGroupToAdd,
  LoadingSpaceRow,
  LoadingSpaceToAddInterface,
  LoadingSpaceType,
  LoadSpace,
  ProjectData,
  RemappedData,
  StopInfo,
  TrueFalse,
  UniKey,
  LoadingSpaceInterface,
} from './import-export-service-types';
import { CsvColumns } from './import-export-service-types';
import { TranslateService } from '@ngx-translate/core';
import {
  airContainerValidKeys,
  defaultValidKeys,
  numberColumns,
  palletValidKeys,
  stringColumns,
  trueFalseColumns,
  validKeysBarrel,
  validKeysBox,
  validKeysPipe,
  stopValidKeys,
  configValidKeys,
} from './import-export-service-sets';
import {
  convertLoadingSpaceDimensions,
  convertProjectDimensions,
  convertCsvLoadsDimensions,
} from '../../shared/helpers/unitRecalculationHelpers';
import { setLocalStorage } from '../../main-panel/utils/localStorageHelpers';

@Injectable({
  providedIn: 'root',
})
export class ImportExportService {
  private project: Project;
  private currentWorkSpace: number;
  public errors$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

  constructor(
    private projectApiService: ProjectApiService,
    private projectService: ProjectService,
    private colorService: ColorsService,
    private datePipe: DatePipe,
    private translate: TranslateService,
  ) {
    this.getProject();
  }

  private getProject(): void {
    this.projectService.currentWorkSpaceIndex.subscribe(
      (currentWorkSpace: number) => (this.currentWorkSpace = currentWorkSpace),
    );
    this.projectService.project.subscribe((project: Project) => (this.project = project));
  }

  public addError(kind: string, row: number, column: string): void {
    const errorTypes = {
      null: 'error_required',
      notANumber: 'error_not_number',
      stopNotUnique: 'is_not_unique',
      stringNotValid: 'error_too_long',
      notBool: 'is_not_y_or_n',
      default: 'error_required',
    };

    const errorKey = errorTypes[kind] || errorTypes['default'];
    const stripUnderscoresColumn: string = column.replace(/_/g, ' ');
    const error = `${
      this.translate.instant('column') as string
    }: <strong>${stripUnderscoresColumn}</strong>, ${
      this.translate.instant('row') as string
    }: <strong>${row}</strong>: ${this.translate.instant(errorKey) as string}.`;
    this.errors$.next([...this.errors$.value, error]);
  }

  public clearErrors(): void {
    this.errors$.next([]);
  }

  public isCsvFile(file: File): boolean {
    return file.type === 'text/csv' || file.name.endsWith('.csv');
  }

  public isXlsxFile(file: File): boolean {
    return (
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.name.endsWith('.xlsx')
    );
  }

  private isStops(data: RemappedData[]): boolean {
    return data.some((row: RemappedData): boolean => {
      return row.isStop;
    });
  }

  private findConfigUnits(data: RemappedData[]): 'cm' | 'lb' {
    const configRow: RemappedData = data.find((row: RemappedData): boolean => {
      return row.isConfig;
    });
    return configRow[CsvColumns.CONFIG_UNITS] as 'cm' | 'lb';
  }

  private convertSpacesIntoUnderScoresForHeaders(row: string[]): string[] {
    const columnNames: string[] = [
      CsvColumns.SPACE_NAME.replace(/_/g, ' '),
      CsvColumns.LOAD_NAME.replace(/_/g, ' '),
      CsvColumns.STOP_NAME.replace(/_/g, ' '),
      CsvColumns.CONFIG_UNITS.replace(/_/g, ' '),
    ];
    const containsColumnName: boolean = row.some((item: string) => {
      return columnNames.includes(item);
    });
    if (containsColumnName) {
      return row.map((item: string): string => {
        return item !== null ? item.replace(/ /g, '_') : item;
      });
    }
    return row;
  }

  private getLoadingSpaceType(loadingSpace: RemappedData): number {
    const isAirContainer: boolean = loadingSpace[CsvColumns.AIR_BASE_B] !== undefined;
    const isPallet: boolean = loadingSpace[CsvColumns.PALLET_MAX_HEIGHT] !== undefined;
    const isSeaContainer: boolean =
      !isAirContainer && !isPallet && loadingSpace[CsvColumns.DOOR_WIDTH] !== undefined;
    const isVehicle: boolean = !isPallet && !isSeaContainer && !isAirContainer;
    switch (true) {
      case isAirContainer:
        return 4;
      case isPallet:
        return 3;
      case isSeaContainer:
        return 2;
      case isVehicle:
        return 1;
      default:
        return 1;
    }
  }

  private getLoadType(loadGroup: RemappedData): number {
    const isWidthDefined: boolean =
      loadGroup[CsvColumns.WIDTH] !== null && loadGroup[CsvColumns.WIDTH] !== undefined;
    const isLengthDefined: boolean =
      loadGroup[CsvColumns.LENGTH] !== null && loadGroup[CsvColumns.LENGTH] !== undefined;
    const isHeightDefined: boolean =
      loadGroup[CsvColumns.HEIGHT] !== null && loadGroup[CsvColumns.HEIGHT] !== undefined;
    const isDiameterDefined: boolean =
      loadGroup[CsvColumns.DIAMETER] !== null && loadGroup[CsvColumns.DIAMETER] !== undefined;
    const isBarrel: boolean =
      isDiameterDefined && isHeightDefined && !isWidthDefined && !isLengthDefined;
    const isPipe: boolean =
      isDiameterDefined && isLengthDefined && !isWidthDefined && !isHeightDefined;
    const isBox: boolean =
      isWidthDefined && isLengthDefined && isHeightDefined && !isDiameterDefined;
    switch (true) {
      case isBarrel:
        return 1;
      case isPipe:
        return 2;
      case isBox:
        return 0;
      default:
        return 0;
    }
  }

  private decorateWithTypes(data: RemappedData[]): RemappedData[] {
    const decoratedData: RemappedData[] = data.map((row: RemappedData): RemappedData => {
      const isLoadingSpace: boolean = row[CsvColumns.SPACE_NAME] !== undefined;
      const isLoad: boolean = row[CsvColumns.LOAD_NAME] !== undefined;
      if (isLoadingSpace) {
        const loadingSpaceType: number = this.getLoadingSpaceType(row);
        const modifiedRow = {
          ...row,
          [CsvColumns.LOADING_TYPE]: loadingSpaceType,
        };
        return modifiedRow as unknown as RemappedData;
      } else if (isLoad) {
        const loadType: number = this.getLoadType(row);
        const modifiedRow = {
          ...row,
          [CsvColumns.LOAD_TYPE]: loadType,
        };
        return modifiedRow as unknown as RemappedData;
      } else {
        return row;
      }
    });
    return decoratedData;
  }

  private remapData(data: string[][]): RemappedData[] {
    const remappedData: RemappedData[] = [];
    let isList = false;
    let lastLoadHeaderIndex: number = null;
    const convertedData: string[][] = data.map((row: string[]): string[] => {
      return this.convertSpacesIntoUnderScoresForHeaders(row);
    });
    convertedData.forEach((row: string[], idx: number): void => {
      const isLoadingSpace: boolean = row.some((item: string): boolean => {
        return item === CsvColumns.SPACE_NAME;
      });
      const isLoad: boolean = row.some((item: string): boolean => {
        return item === CsvColumns.LOAD_NAME;
      });
      const isStop: boolean = row.some((item: string): boolean => {
        return item === CsvColumns.STOP_NAME;
      });
      const isConfig: boolean = row.some((item: string): boolean => {
        return item === CsvColumns.CONFIG_UNITS;
      });

      if (isConfig) {
        isList = false;
        lastLoadHeaderIndex = null;
        remappedData.push({
          isConfig: true,
          ...this.createObjectFromArrays(convertedData[idx], convertedData[idx + 1]),
        });
      }
      if (isStop) {
        isList = false;
        lastLoadHeaderIndex = null;
        remappedData.push({
          isStop: true,
          ...this.createObjectFromArrays(convertedData[idx], convertedData[idx + 1]),
        });
      }
      if (isLoadingSpace) {
        isList = false;
        lastLoadHeaderIndex = null;
        remappedData.push({
          isLoadingSpace: true,
          ...this.createObjectFromArrays(convertedData[idx], convertedData[idx + 1]),
        });
      }
      if (isLoad || isList) {
        if (!isList) {
          lastLoadHeaderIndex = idx;
          remappedData.push({
            isLoad: true,
            ...this.createObjectFromArrays(convertedData[idx], convertedData[idx + 1]),
          });
          isList = true;
        } else {
          if (idx + 1 < convertedData.length) {
            if (
              !convertedData[idx + 1].includes(CsvColumns.SPACE_NAME) &&
              !convertedData[idx + 1].includes(CsvColumns.CONFIG_UNITS) &&
              !convertedData[idx + 1].includes(CsvColumns.STOP_NAME)
            ) {
              remappedData.push({
                isLoad: true,
                ...this.createObjectFromArrays(
                  convertedData[lastLoadHeaderIndex],
                  convertedData[idx + 1],
                ),
              });
            }
          }
        }
      }
    });
    const dataDecoratedByType: RemappedData[] = this.decorateWithTypes(remappedData);
    return this.filterNullValues(dataDecoratedByType);
  }

  private filterNullValues(data: RemappedData[]): RemappedData[] {
    return data.map((item: RemappedData): RemappedData => {
      const newItem = {};
      for (const key in item) {
        if (item[key] !== null) {
          newItem[key] = item[key];
        }
      }
      return newItem;
    });
  }

  private remapToStops(data: RemappedData[]): StopInfo[] {
    return data
      .filter((item: RemappedData) => item.isStop)
      .map((stop: RemappedData) => ({
        address:
          stop[CsvColumns.STREET] !== undefined && stop[CsvColumns.STREET] !== null
            ? stop[CsvColumns.STREET].toString()
            : undefined,
        city:
          stop[CsvColumns.CITY] !== undefined && stop[CsvColumns.CITY] !== null
            ? stop[CsvColumns.CITY].toString()
            : undefined,
        country:
          stop[CsvColumns.COUNTRY] !== undefined && stop[CsvColumns.COUNTRY] !== null
            ? stop[CsvColumns.COUNTRY].toString()
            : undefined,
        postalCode:
          stop[CsvColumns.POSTAL_CODE] !== undefined && stop[CsvColumns.POSTAL_CODE] !== null
            ? stop[CsvColumns.POSTAL_CODE].toString()
            : undefined,
        name:
          stop[CsvColumns.STOP_NAME] !== undefined && stop[CsvColumns.STOP_NAME] !== null
            ? stop[CsvColumns.STOP_NAME].toString()
            : undefined,
      }));
  }

  private createWorkSpaces(data: RemappedData[]): CurrentWorkSpace[] {
    const workSpaces: CurrentWorkSpace[] = [];
    const stopsList: StopInfo[] = this.remapToStops(data);
    const isStops: boolean = stopsList.length > 0;
    let currentWorkSpace: CurrentWorkSpace = {
      loadingSpace: {
        loadGroups: [],
      },
      loadingSpaceSet: {
        loadGroups: [],
      },
      unloadingSpace: [],
    };
    let isLoadingSpaceSet = null;
    const firstLoadingSpaceIndex: number = data.findIndex((row: RemappedData): boolean => {
      return row.isLoadingSpace;
    });
    let stopNumber = 0;
    let oldStopNumber = 0;
    let rowGroup = [];
    let isStopAfterStop = null;
    const unloadingGroup = [];
    data.forEach((row: RemappedData, idx: number): void => {
      if (row.isStop) {
        oldStopNumber = stopNumber;
        stopNumber = stopNumber + 1;
        const target: LoadSpace = isLoadingSpaceSet
          ? currentWorkSpace.loadingSpaceSet
          : currentWorkSpace.loadingSpace;
        const isNewStopNumber: boolean = stopNumber !== oldStopNumber;
        if (isNewStopNumber) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          target.loadGroups.push(rowGroup);
          rowGroup = [];
        }
        if (isStopAfterStop === false) {
          isStopAfterStop = true;
        } else {
          if (isStopAfterStop === null) {
            isStopAfterStop = false;
          } else if (isStopAfterStop === false) {
            isStopAfterStop = true;
          }
        }
      }
      if (
        row.isLoadingSpace &&
        !((row[CsvColumns.IS_SET] as unknown as string) === TrueFalse.TRUE)
      ) {
        stopNumber = 0;
        isLoadingSpaceSet = null;
        currentWorkSpace = {
          loadingSpace: {
            ...row,
            loadGroups: [],
          },
          unloadingSpace: [],
        };
      }
      if (row.isLoadingSpace && (row[CsvColumns.IS_SET] as unknown as string) === TrueFalse.TRUE) {
        stopNumber = 0;
        if (isLoadingSpaceSet === null) {
          isLoadingSpaceSet = false;
          currentWorkSpace.loadingSpace = {
            ...row,
            loadGroups: [],
          };
        } else {
          isLoadingSpaceSet = true;
          currentWorkSpace.loadingSpaceSet = {
            ...row,
            loadGroups: [],
          };
        }
      }
      if (isStopAfterStop === true) {
        isStopAfterStop = null;
      }
      if (row.isLoad) {
        isStopAfterStop = null;
        const target: LoadSpace = isLoadingSpaceSet
          ? currentWorkSpace.loadingSpaceSet
          : currentWorkSpace.loadingSpace;
        if (isStops) {
          rowGroup.push(row as unknown as LoadGroup);
        } else {
          target.loadGroups.push(row as unknown as LoadGroup);
        }
      }
      const isDataEnd: boolean = idx + 1 === data.length;
      const isNextLoadingSpaceSet: boolean =
        data[idx + 1]?.isLoadingSpace !== undefined &&
        data[idx + 1][CsvColumns.IS_SET] === TrueFalse.TRUE &&
        isLoadingSpaceSet;
      const isNextLoadingSpaceASet: boolean =
        data[idx + 1]?.isLoadingSpace !== undefined &&
        data[idx + 1][CsvColumns.IS_SET] === TrueFalse.TRUE &&
        isLoadingSpaceSet === null;
      const isNextLoadingSpace: boolean =
        data[idx + 1]?.isLoadingSpace !== undefined &&
        data[idx + 1][CsvColumns.IS_SET] === TrueFalse.FALSE;
      const isNextFirst: boolean = idx + 1 === firstLoadingSpaceIndex;
      if (
        isDataEnd ||
        ((isNextLoadingSpaceSet || isNextLoadingSpace || isNextLoadingSpaceASet) && !isNextFirst)
      ) {
        isLoadingSpaceSet = null;
        currentWorkSpace.stops = stopsList;
        currentWorkSpace.unloadingSpace = unloadingGroup;
        workSpaces.push(currentWorkSpace);
        currentWorkSpace = {
          loadingSpace: {
            loadGroups: [],
          },
          loadingSpaceSet: {
            loadGroups: [],
          },
          unloadingSpace: [],
        };
      }
    });
    return workSpaces;
  }

  private createObjectFromArrays(header: string[], data: string[]): any {
    return header.reduce((obj, key: string, index: number) => {
      obj[key] = data[index];
      return obj;
    }, {});
  }

  private decorateIsSet(data: RemappedData[]): RemappedData[] {
    return data.map((row: RemappedData): RemappedData => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (
        (row.isLoadingSpace &&
          ((row[CsvColumns.LOADING_TYPE] as unknown as number) === LoadingSpaceType.AirContainer ||
            (row[CsvColumns.LOADING_TYPE] as unknown as number) === LoadingSpaceType.Pallet)) ||
        (row[CsvColumns.LOADING_TYPE] as unknown as number) === LoadingSpaceType.Vehicle ||
        (row[CsvColumns.LOADING_TYPE] as unknown as number) === LoadingSpaceType.SeaContainer
      ) {
        return { ...row, IS_SET: TrueFalse.FALSE };
      }
      return row;
    });
  }

  public extractData(data: RemappedData[]): ProjectData {
    const isStops: boolean = this.isStops(data);
    const unit: string = this.findConfigUnits(data);
    data = this.decorateIsSet(data);
    const stops: CurrentWorkSpace[] = this.createWorkSpaces(data);
    return {
      isStops: isStops,
      unit: unit,
      stops: stops,
    };
  }

  private checkFileRemappedDataValidity(data: RemappedData[]): boolean {
    const isConfig: boolean = data.some((row: RemappedData): boolean => {
      return row.isConfig;
    });
    const isLoadingSpace: boolean = data.some((row: RemappedData): boolean => {
      return row.isLoadingSpace;
    });
    const isLoad: boolean = data.some((row: RemappedData): boolean => {
      return row.isLoad;
    });
    return isConfig && (isLoadingSpace || isLoad);
  }

  private decorateWithOriginalIndex(data: RemappedData[]): RemappedData[] {
    let isLoadList = false;
    let counter = 0;
    const indexedData: RemappedData[] = data.map((item: RemappedData) => {
      let addRows: number = item.isConfig || item.isStop || item.isLoadingSpace ? 2 : 1;
      if (item.isConfig || item.isStop || item.isLoadingSpace) {
        isLoadList = false;
      }
      if (item.isLoad && !isLoadList) {
        isLoadList = true;
        addRows = 2;
      }
      counter = counter + addRows;
      return {
        ...item,
        originalIndex: counter,
      } as unknown as RemappedData;
    });
    return indexedData;
  }

  private checkRequiredKeys(
    mustBeValidKeys: string[],
    item: RemappedData,
    checkNull = false,
  ): boolean {
    let allValid: boolean = null;
    mustBeValidKeys.forEach((key: string): void => {
      let isKeysValid = true;
      const isEmpty = !this.isNotEmpty(item[key]);
      if (checkNull && isEmpty) {
        isKeysValid = true;
      } else {
        switch (true) {
          case stringColumns.includes(key):
            isKeysValid = this.isAString(item[key]) || this.isANumber(item[key]);
            if (!isKeysValid && !isEmpty) {
              this.addError('stringNotValid', item.originalIndex as unknown as number, key);
            }
            if (isEmpty) {
              this.addError('null', item.originalIndex as unknown as number, key);
            }
            break;
          case numberColumns.includes(key):
            isKeysValid = this.isANumber(item[key]);
            if (!isKeysValid && !isEmpty) {
              this.addError('notANumber', item.originalIndex as unknown as number, key);
            }
            if (isEmpty) {
              this.addError('null', item.originalIndex as unknown as number, key);
            }
            break;
          case trueFalseColumns.includes(key):
            isKeysValid = this.isABoolean(item[key]);
            if (!isKeysValid) {
              this.addError('notBool', item.originalIndex as unknown as number, key);
            }
            break;
          case !stringColumns.includes(key) &&
            !numberColumns.includes(key) &&
            !trueFalseColumns.includes(key):
            isKeysValid = this.isNotEmpty(item[key]);
            if (!isKeysValid) {
              this.addError('default', item.originalIndex as unknown as number, key);
            }
            break;
        }
      }
      allValid = isKeysValid && allValid === false ? false : isKeysValid;
    });
    return allValid;
  }

  private checkLoadingSpacesValidity(data: RemappedData[]): boolean {
    const loadingSpaces: RemappedData[] = data.filter((row: RemappedData): boolean => {
      return row.isLoadingSpace;
    });
    const isValid: boolean = loadingSpaces.every((loadingSpace: RemappedData): boolean => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const isAirContainer: boolean =
        (loadingSpace[CsvColumns.LOADING_TYPE] as unknown as number) ===
        (LoadingSpaceType.AirContainer as unknown as number);
      const isPallet: boolean =
        (loadingSpace[CsvColumns.LOADING_TYPE] as unknown as number) ===
        (LoadingSpaceType.Pallet as unknown as number);
      const mustBeValidKeys: string[] = isAirContainer
        ? airContainerValidKeys
        : isPallet
        ? palletValidKeys
        : defaultValidKeys;
      return this.checkRequiredKeys(mustBeValidKeys, loadingSpace);
    });
    return isValid && loadingSpaces.length > 0;
  }

  private checkNotRequiredKeys(data: RemappedData[]): boolean {
    const requiredKeys: string[] = [
      ...defaultValidKeys,
      ...palletValidKeys,
      ...airContainerValidKeys,
      ...stopValidKeys,
      ...configValidKeys,
      ...validKeysBox,
      ...validKeysBarrel,
      ...validKeysPipe,
    ];
    const allKeys: string[] = [...stringColumns, ...numberColumns, ...trueFalseColumns];
    const notRequiredKeys: string[] = allKeys.filter((key: string): boolean => {
      return !requiredKeys.includes(key);
    });
    const isValid: boolean = data.every((row: RemappedData): boolean => {
      const notRequiredCheck: boolean = this.checkRequiredKeys(notRequiredKeys, row, true);
      return notRequiredCheck;
    });
    return isValid;
  }

  private checkLoadGroupsValidity(data: RemappedData[]): boolean {
    const loadGroups: RemappedData[] = data.filter((row: RemappedData): boolean => {
      return row.isLoad;
    });
    const isValid: boolean = loadGroups.every((loadGroup: RemappedData): boolean => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const mustBeValidKeys: string[] = this.getMustBeValidKeys(loadGroup);
      return this.checkRequiredKeys(mustBeValidKeys, loadGroup);
    });
    return isValid;
  }

  private checkStopsValidity(data: RemappedData[]): boolean {
    const stops: RemappedData[] = data.filter((row: RemappedData): boolean => {
      return row.isStop;
    });
    const isValid: boolean = stops.every((stop: RemappedData): boolean => {
      return this.checkRequiredKeys(stopValidKeys, stop);
    });
    return isValid;
  }

  private checkConfigValidity(data: RemappedData[]): boolean {
    const config: RemappedData[] = data.filter((row: RemappedData): boolean => {
      return row.isConfig;
    });
    const isValid: boolean = config.every((configRow: RemappedData): boolean => {
      return this.checkRequiredKeys(configValidKeys, configRow);
    });
    return isValid;
  }

  private getMustBeValidKeys(loadGroup: RemappedData): string[] {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return loadGroup[CsvColumns.LOAD_TYPE] === 1
      ? validKeysBarrel
      : // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      loadGroup[CsvColumns.LOAD_TYPE] === 2
      ? validKeysPipe
      : validKeysBox;
  }

  private isAString(value: any): boolean {
    return typeof value === 'string' && value?.length <= 256;
  }

  private isANumber(value: any): boolean {
    return !isNaN(Number(value));
  }

  private isABoolean(value: any): boolean {
    return value === TrueFalse.TRUE || value === TrueFalse.FALSE;
  }

  private isNotEmpty(value: any): boolean {
    return value !== null && value !== undefined;
  }

  private checkStopsNamesUniqueness(data: RemappedData[]): boolean {
    const stops: RemappedData[] = data.filter((row: RemappedData): boolean => {
      return row.isStop;
    });
    const isAllStopNamesDifferent: boolean = stops.every((stop: RemappedData, index: number) => {
      const stopName: string = stop[CsvColumns.STOP_NAME].toString() as unknown as string;
      const isStopNameUnique: boolean =
        stops.findIndex((stop: RemappedData): boolean => {
          const stopStopName = stop[CsvColumns.STOP_NAME].toString();
          return stopStopName === stopName;
        }) === index;
      if (!isStopNameUnique) {
        this.addError('stop', null, stopName);
      }
      return isStopNameUnique;
    });
    return isAllStopNamesDifferent;
  }

  private checkValidity(remappedData: RemappedData[]): boolean {
    const indexedData: RemappedData[] = this.decorateWithOriginalIndex(remappedData);
    const isValid: boolean = this.checkFileRemappedDataValidity(remappedData);
    const isRemappedDataContainLoadGroups: boolean = remappedData.some(
      (row: RemappedData): boolean => {
        return row.isLoad;
      },
    );
    const isRemappedDataContainLoadingSpaces: boolean = remappedData.some(
      (row: RemappedData): boolean => {
        return row.isLoadingSpace;
      },
    );

    const isRemappedDataContainStops: boolean = remappedData.some((row: RemappedData): boolean => {
      return row.isStop;
    });

    let isValidLoadingSpaces = false;
    let isValidLoadGroups = false;
    let isStopsValid = true;
    let isConfigValid = false;
    let isNotRequiredValid = true;
    if (isValid) {
      isConfigValid = this.checkConfigValidity(indexedData);
      isValidLoadingSpaces = this.checkLoadingSpacesValidity(indexedData);
      isValidLoadGroups = this.checkLoadGroupsValidity(indexedData);
      isNotRequiredValid = this.checkNotRequiredKeys(indexedData);
      if (isRemappedDataContainStops) {
        isStopsValid = this.checkStopsNamesUniqueness(indexedData);
        isStopsValid = isStopsValid === false ? false : this.checkStopsValidity(indexedData);
      }
    }

    let isEverythingValid: boolean;
    if (isRemappedDataContainLoadGroups && !isRemappedDataContainLoadingSpaces) {
      isEverythingValid = isValid && isValidLoadGroups;
    } else if (!isRemappedDataContainLoadGroups && isRemappedDataContainLoadingSpaces) {
      isEverythingValid = isValid && isValidLoadingSpaces;
    } else if (isRemappedDataContainLoadGroups && isRemappedDataContainLoadingSpaces) {
      isEverythingValid = isValid && isValidLoadingSpaces && isValidLoadGroups;
    } else {
      isEverythingValid = false;
    }
    isEverythingValid = isEverythingValid && isConfigValid && isNotRequiredValid;
    return isRemappedDataContainStops ? isEverythingValid && isStopsValid : isEverythingValid;
  }

  public parseFile(file: File): Observable<ProjectData> {
    return new Observable((observer: Subscriber<ProjectData>): void => {
      if (this.isCsvFile(file)) {
        this.parseCSV(file).subscribe((result: string[][]): void => {
          const remappedData: RemappedData[] = this.remapData(result);
          const isValid: boolean = this.checkValidity(remappedData);
          if (!isValid) {
            // observer.error(new Error('Invalid file format'));
          } else {
            const data: ProjectData = this.extractData(remappedData);
            observer.next(data);
            observer.complete();
          }
        });
      } else if (this.isXlsxFile(file)) {
        this.parseXLSX(file).subscribe((result: string[][]): void => {
          let remappedData: RemappedData[] = this.remapData(result);
          remappedData = this.filterUndefined(remappedData);
          const isValid: boolean = this.checkValidity(remappedData);
          if (!isValid) {
            // observer.error(new Error('Invalid file format'));
          } else {
            const data: ProjectData = this.extractData(remappedData);
            observer.next(data);
            observer.complete();
          }
        });
      } else {
        observer.error(new Error('Unsupported file type'));
      }
    });
  }

  private filterUndefined(remappedData: RemappedData[]): RemappedData[] {
    return remappedData.filter(
      (item: RemappedData) =>
        !(item.isLoad && item[CsvColumns.LOAD_NAME] === undefined) &&
        !(item.isLoadingSpace && item[CsvColumns.SPACE_NAME] === undefined),
    );
  }

  private convertLoadGroup(loadGroup: UniKey): ConvertedLoadGroup {
    const isPipe: boolean = loadGroup[CsvColumns.LOAD_TYPE] === 2;
    const isBarrel: boolean = loadGroup[CsvColumns.LOAD_TYPE] === 1;
    const changedWidth: string | number =
      isPipe || isBarrel ? loadGroup[CsvColumns.DIAMETER] : loadGroup[CsvColumns.WIDTH];
    const changedHeight: string | number = isPipe
      ? loadGroup[CsvColumns.DIAMETER]
      : loadGroup[CsvColumns.HEIGHT];
    const isAllValuesInCenterOfMassDefined: boolean = [
      loadGroup[CsvColumns.CENTER_OF_MASS_X],
      loadGroup[CsvColumns.CENTER_OF_MASS_Y],
      loadGroup[CsvColumns.CENTER_OF_MASS_Z],
    ].every((value: string | number) => value !== undefined && value !== null);
    const calculateCenterOfMass = {
      x: (changedWidth as number) / 2,
      y: (changedHeight as number) / 2,
      z: 0,
    };
    const changedCenterOfMAss = !isAllValuesInCenterOfMassDefined
      ? calculateCenterOfMass
      : {
          x: loadGroup[CsvColumns.CENTER_OF_MASS_X],
          y: loadGroup[CsvColumns.CENTER_OF_MASS_Y],
          z: loadGroup[CsvColumns.CENTER_OF_MASS_Z],
        };
    const convertedLoadGroup: ConvertedLoadGroup = {
      load: {
        name: loadGroup[CsvColumns.LOAD_NAME].toString(),
        loadType: loadGroup[CsvColumns.LOAD_TYPE],
        width: changedWidth,
        length: loadGroup[CsvColumns.LENGTH],
        height: changedHeight,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        diameter: loadGroup[CsvColumns.DIAMETER],
        weight: loadGroup[CsvColumns.WEIGHT],
        stacking:
          loadGroup[CsvColumns.STACKING] === undefined
            ? false
            : loadGroup[CsvColumns.STACKING] === TrueFalse.TRUE,
        stackingOptions: {
          maxLoadsOnTop: loadGroup[CsvColumns.MAX_LOADS_ON_TOP],
          maxWeightOnTop: loadGroup[CsvColumns.MAX_WEIGHT_ON_TOP],
          alongFloor: loadGroup[CsvColumns.ALONG_FLOOR] === TrueFalse.TRUE,
          pctSupport: loadGroup[CsvColumns.PCT_SUPPORT],
          formation: loadGroup[CsvColumns.FORMATION],
        },
        destination: {
          stopId: loadGroup[CsvColumns.UNLOADING_STOP]?.toString(),
        },
        allowOutOfSpace: loadGroup[CsvColumns.ALLOW_OUT_OF_SPACE] === TrueFalse.TRUE,
        allowToRotate: !(loadGroup[CsvColumns.ROTATE_ON_BASE] === TrueFalse.FALSE),
        rotateOnBase:
          loadGroup[CsvColumns.ROTATE_ON_BASE] === TrueFalse.TRUE ||
          (loadGroup[CsvColumns.ALLOW_OUT_OF_SPACE] !== TrueFalse.TRUE &&
            loadGroup[CsvColumns.ROTATE_FREELY] !== TrueFalse.TRUE),
        rotateFreely: loadGroup[CsvColumns.ROTATE_FREELY] === TrueFalse.TRUE,
        centerOfMass: changedCenterOfMAss,
        loadId: loadGroup[CsvColumns.LOAD_ID],
        stopId: loadGroup.stopId,
        color: this.colorService.getColor(),
      },
      quantity: loadGroup[CsvColumns.QUANTITY] as number,
    };
    return convertedLoadGroup;
  }

  private convertLoadingSpace(
    loadingSpace: UniKey,
    loadingSpaceSet: UniKey,
  ): LoadingSpaceToAddInterface {
    const isVehicle: boolean = loadingSpace[CsvColumns.LOADING_TYPE] === LoadingSpaceType.Vehicle;
    const isContainer: boolean =
      loadingSpace[CsvColumns.LOADING_TYPE] === LoadingSpaceType.SeaContainer;
    const isPallet: boolean = loadingSpace[CsvColumns.LOADING_TYPE] === LoadingSpaceType.Pallet;
    const isAirContainer: boolean =
      loadingSpace[CsvColumns.LOADING_TYPE] === LoadingSpaceType.AirContainer;
    let loadingSpaceBody;
    const axis =
      loadingSpace.AXIS_DISTANCE_FROM_FRONT !== undefined
        ? [
            {
              distanceFromSpaceFront: loadingSpace[CsvColumns.AXIS_DISTANCE_FROM_FRONT],
              emptySpaceLoad: loadingSpace[CsvColumns.AXIS_EMPTY_SPACE_LOAD].toString(),
              //loadingSpacePart: loadingSpace[CsvColumns.AXIS_LOADING_SPACE_PART],
              loadingSpacePart:
                loadingSpace[CsvColumns.AXIS_DISTANCE_FROM_FRONT] !== undefined ? 1 : undefined,
              maxLoad: loadingSpace[CsvColumns.AXIS_MAX_LOAD].toString(),
            },
            {
              distanceFromSpaceFront: loadingSpace[CsvColumns.AXIS_2_DISTANCE_FROM_FRONT],
              emptySpaceLoad: loadingSpace[CsvColumns.AXIS_2_EMPTY_SPACE_LOAD].toString(),
              loadingSpacePart:
                loadingSpace[CsvColumns.AXIS_2_DISTANCE_FROM_FRONT] !== undefined ? 1 : undefined,
              // loadingSpacePart: loadingSpace[CsvColumns.AXIS_2_LOADING_SPACE_PART],
              maxLoad: loadingSpace[CsvColumns.AXIS_2_MAX_LOAD].toString(),
            },
          ]
        : [];
    if (loadingSpace.AXIS_3_DISTANCE_FROM_FRONT !== undefined) {
      axis.push({
        distanceFromSpaceFront: loadingSpace[CsvColumns.AXIS_3_DISTANCE_FROM_FRONT],
        emptySpaceLoad:
          loadingSpace[CsvColumns.AXIS_3_EMPTY_SPACE_LOAD] !== undefined
            ? loadingSpace[CsvColumns.AXIS_3_EMPTY_SPACE_LOAD].toString()
            : undefined,
        loadingSpacePart:
          loadingSpace[CsvColumns.AXIS_3_DISTANCE_FROM_FRONT] !== undefined ? 1 : undefined,
        // loadingSpacePart: loadingSpace[CsvColumns.AXIS_3_LOADING_SPACE_PART],
        maxLoad:
          loadingSpace[CsvColumns.AXIS_3_MAX_LOAD] !== undefined
            ? loadingSpace[CsvColumns.AXIS_3_MAX_LOAD].toString()
            : undefined,
      });
    }
    if (isVehicle || isContainer) {
      loadingSpaceBody = {
        name: loadingSpace[CsvColumns.SPACE_NAME].toString(),
        length: loadingSpace[CsvColumns.LENGTH],
        width: loadingSpace[CsvColumns.WIDTH],
        height: loadingSpace[CsvColumns.HEIGHT],
        limit: loadingSpace[CsvColumns.LIMIT] !== undefined ? loadingSpace[CsvColumns.LIMIT] : 0,
        set: loadingSpace[CsvColumns.IS_SET] === TrueFalse.TRUE,
        type: loadingSpace[CsvColumns.LOADING_TYPE],
        loadGroups: undefined,
        loadGroupsSet: undefined,
        trailerLoadingSide: loadingSpace[CsvColumns.LOADING_SIDE],
        semiTrailerLoadingSide: loadingSpace[CsvColumns.LOADING_SIDE],
        axis,
        coolerTrailerWidth: loadingSpace[CsvColumns.COOLER_WIDTH],
        coolerTrailerHeight: loadingSpace[CsvColumns.COOLER_HEIGHT],
        coolerTrailerLength: loadingSpace[CsvColumns.COOLER_LENGTH],
        coolerTrailerFloorHeight: loadingSpace[CsvColumns.COOLER_FLOOR_HEIGHT],
        wheelTrailerWidth: loadingSpace[CsvColumns.WHEEL_WIDTH],
        wheelTrailerHeight: loadingSpace[CsvColumns.WHEEL_HEIGHT],
        wheelTrailerLength: loadingSpace[CsvColumns.WHEEL_LENGTH],
        wheelTrailerDistance: loadingSpace[CsvColumns.WHEEL_DISTANCE],
        varioTrailerFloorHeight: loadingSpace[CsvColumns.VARIO_FLOOR_HEIGHT],
        varioTrailerFloorThickness: loadingSpace[CsvColumns.VARIO_FLOOR_THICKNESS],
        varioTrailerFloorCapacity: loadingSpace[CsvColumns.VARIO_FLOOR_CAPACITY],
        trailerDoorHeight: loadingSpace[CsvColumns.DOOR_HEIGHT],
        trailerDoorWidth: loadingSpace[CsvColumns.DOOR_WIDTH],
        gravityShowCenter: loadingSpace[CsvColumns.GRAVITY_SHOW_CENTER] === TrueFalse.TRUE,
        gravityTrailerLengthTolerance: loadingSpace[CsvColumns.GRAVITY_LENGTH_TOLERANCE],
        gravityTrailerWidthTolerance: loadingSpace[CsvColumns.GRAVITY_WIDTH_TOLERANCE],
        trailerContainerWeight: loadingSpace[CsvColumns.GRAVITY_CONTAINER_WEIGHT],
      };
      if (isContainer) {
        const containerDoors = {
          trailerDoorHeight: loadingSpace[CsvColumns.DOOR_WIDTH],
          trailerDoorWidth: loadingSpace[CsvColumns.DOOR_HEIGHT],
        };
        loadingSpaceBody = { ...loadingSpaceBody, ...containerDoors };
      }
    } else if (isPallet) {
      loadingSpaceBody = {
        name: loadingSpace[CsvColumns.SPACE_NAME].toString(),
        length: loadingSpace[CsvColumns.LENGTH],
        width: loadingSpace[CsvColumns.WIDTH],
        height: loadingSpace[CsvColumns.PALLET_MAX_HEIGHT],
        limit: loadingSpace[CsvColumns.LIMIT] !== undefined ? loadingSpace[CsvColumns.LIMIT] : 0,
        type: loadingSpace[CsvColumns.LOADING_TYPE],
        loadGroups: undefined,
        loadGroupsSet: undefined,
        palletWeight: loadingSpace[CsvColumns.PALLET_WEIGHT],
        palletMaxHeight: loadingSpace[CsvColumns.PALLET_MAX_HEIGHT],
        palletMaxWeight: loadingSpace[CsvColumns.PALLET_MAX_WEIGHT]
          ? loadingSpace[CsvColumns.PALLET_MAX_WEIGHT]
          : null,
        palletHeight: loadingSpace[CsvColumns.PALLET_HEIGHT]
          ? loadingSpace[CsvColumns.PALLET_HEIGHT]
          : null,
      };
    } else if (isAirContainer) {
      loadingSpaceBody = {
        name: loadingSpace[CsvColumns.SPACE_NAME].toString(),
        length: loadingSpace[CsvColumns.AIR_LENGTH_A],
        width: loadingSpace[CsvColumns.AIR_WIDTH],
        height: loadingSpace[CsvColumns.AIR_HEIGHT_C],
        limit: loadingSpace[CsvColumns.LIMIT] !== undefined ? loadingSpace[CsvColumns.LIMIT] : 0,
        type: loadingSpace[CsvColumns.LOADING_TYPE],
        loadGroups: undefined,
        loadGroupsSet: undefined,
        airBaseB: loadingSpace[CsvColumns.AIR_BASE_B],
        airHeightC: loadingSpace[CsvColumns.AIR_HEIGHT_C],
        airHeightD: loadingSpace[CsvColumns.AIR_HEIGHT_D],
        airLengthA: loadingSpace[CsvColumns.AIR_LENGTH_A],
        airType: loadingSpace[CsvColumns.AIR_TYPE],
        airWidth: loadingSpace[CsvColumns.AIR_WIDTH],
        gravityShowCenter: loadingSpace[CsvColumns.GRAVITY_SHOW_CENTER] === TrueFalse.TRUE,
        gravityTrailerLengthTolerance: loadingSpace[CsvColumns.GRAVITY_LENGTH_TOLERANCE],
        gravityTrailerWidthTolerance: loadingSpace[CsvColumns.GRAVITY_WIDTH_TOLERANCE],
        trailerContainerWeight: loadingSpace[CsvColumns.GRAVITY_CONTAINER_WEIGHT],
      };
    }
    const convertedLoadingSpace: LoadingSpaceToAddInterface = {
      currentWorkSpaceId: 1,
      newLoadingSpaces: {
        quantity: 1,
        loadingSpace: loadingSpaceBody,
        stops: loadingSpace.stops as unknown as StopInfo[],
      },
    };
    if (
      loadingSpace[CsvColumns.IS_SET] === TrueFalse.TRUE &&
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      loadingSpaceSet?.loadGroups?.length > 0
    ) {
      convertedLoadingSpace.newLoadingSpaces.loadingSpace = {
        ...convertedLoadingSpace.newLoadingSpaces.loadingSpace,
        width1: loadingSpaceSet[CsvColumns.WIDTH],
        height1: loadingSpaceSet[CsvColumns.HEIGHT],
        length1: loadingSpaceSet[CsvColumns.LENGTH],
        limit1: loadingSpace[CsvColumns.LIMIT] !== undefined ? loadingSpace[CsvColumns.LIMIT] : 0,
        semiTrailerLoadingSide: loadingSpaceSet[CsvColumns.LOADING_SIDE],
        coolerSemiTrailerWidth: loadingSpaceSet[CsvColumns.COOLER_WIDTH],
        coolerSemiTrailerHeight: loadingSpaceSet[CsvColumns.COOLER_HEIGHT],
        coolerSemiTrailerLength: loadingSpaceSet[CsvColumns.COOLER_LENGTH],
        coolerSemiTrailerFloorHeight: loadingSpaceSet[CsvColumns.COOLER_FLOOR_HEIGHT],
        wheelSemiTrailerWidth: loadingSpaceSet[CsvColumns.WHEEL_WIDTH],
        wheelSemiTrailerHeight: loadingSpaceSet[CsvColumns.WHEEL_HEIGHT],
        wheelSemiTrailerDistance: loadingSpaceSet[CsvColumns.WHEEL_DISTANCE],
        wheelSemiTrailerLength: loadingSpaceSet[CsvColumns.WHEEL_LENGTH],
        semiTrailerDoorWidth: loadingSpaceSet[CsvColumns.DOOR_WIDTH],
        varioSemiTrailerFloorHeight: loadingSpaceSet[CsvColumns.VARIO_FLOOR_HEIGHT],
        semiTrailerDoorHeight: loadingSpaceSet[CsvColumns.DOOR_HEIGHT],
        varioSemiTrailerFloorThickness: loadingSpaceSet[CsvColumns.VARIO_FLOOR_THICKNESS],
        varioSemiTrailerFloorCapacity: loadingSpaceSet[CsvColumns.VARIO_FLOOR_CAPACITY],
        gravitySemiTrailerLengthTolerance: loadingSpaceSet[CsvColumns.GRAVITY_LENGTH_TOLERANCE],
        gravitySemiTrailerWidthTolerance: loadingSpaceSet[CsvColumns.GRAVITY_WIDTH_TOLERANCE],
        semiTrailerContainerWeight: loadingSpaceSet[CsvColumns.GRAVITY_CONTAINER_WEIGHT],
      };
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const loadGroupsSet: LoadGroup[] = loadingSpaceSet.loadGroups.map((loadGroup: LoadGroup) => {
        const convertedLoadGroup: ConvertedLoadGroup = this.convertLoadGroup(
          loadGroup as unknown as UniKey,
        );
        return convertedLoadGroup;
      });
      convertedLoadingSpace.newLoadingSpaces.loadingSpace.loadGroupsSet = loadGroupsSet;
    }
    return convertedLoadingSpace;
  }

  public convertDataToApi(data: ProjectData): LoadingSpaceToAddInterface[] {
    const stops: StopInfo[] = data.stops[0].stops.map((stop: StopInfo) => {
      return {
        ...stop,
        name: stop.name.toString(),
      };
    });
    const isStops: boolean = data.isStops;
    const convertedData: LoadingSpaceToAddInterface[] = data.stops.map(
      (loadingSpace: CurrentWorkSpace) => {
        const loadingSpaceSet = loadingSpace.loadingSpaceSet;
        const loadingSpace1 = { ...loadingSpace.loadingSpace, stops };
        const loadGroups: any = loadingSpace1.loadGroups.map(
          (loadGroup: LoadGroup | LoadGroup[], idx: number) => {
            if (isStops) {
              const loadGroup2: LoadGroup[] = loadGroup as LoadGroup[];
              const convertedGroup: ConvertedLoadGroup[] = loadGroup2.map((group: LoadGroup) => {
                group.stopId = idx + 1;
                return this.convertLoadGroup(group as unknown as UniKey);
              });
              return convertedGroup;
            } else {
              return this.convertLoadGroup(loadGroup as unknown as UniKey);
            }
          },
        );
        const convertedLoadingSpace: LoadingSpaceToAddInterface = this.convertLoadingSpace(
          loadingSpace1 as unknown as UniKey,
          loadingSpaceSet as unknown as UniKey,
        );
        if (convertedLoadingSpace.newLoadingSpaces.loadingSpace === undefined) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          convertedLoadingSpace.newLoadingSpaces.loadingSpace = { loadGroups: [] };
        }
        convertedLoadingSpace.newLoadingSpaces.loadingSpace.loadGroups = loadGroups;
        const convertedUnloadingSpace: ConvertedLoadGroup[] = loadingSpace.unloadingSpace.map(
          (unloadingSpace: any) => {
            let convertedGroup: ConvertedLoadGroup = this.convertLoadGroup(
              unloadingSpace as unknown as UniKey,
            );
            convertedGroup = {
              load: {
                name: convertedGroup.load.name.toString(),
                loadId: convertedGroup.load.loadId,
                stopId: convertedGroup.load.stopId,
              } as unknown as LoadGroup,
            } as unknown as ConvertedLoadGroup;
            return convertedGroup;
          },
        );
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        convertedLoadingSpace.newLoadingSpaces.loadingSpace.unloadingSpace =
          convertedUnloadingSpace;
        return convertedLoadingSpace;
      },
    );
    return convertedData;
  }

  public prepareUserLoadsAndSpaces(data: LoadingSpaceToAddInterface[]): {
    userLoads: LoadGroup[];
    userLoadingSpaces: LoadingSpaceToAddInterface[];
  } {
    const userLoads: any = data
      .map((loadingSpace: LoadingSpaceToAddInterface) => {
        const loadGroups: LoadGroup[] = loadingSpace.newLoadingSpaces.loadingSpace.loadGroups;
        return loadGroups;
      })
      .map((loadGroups: LoadGroup[]) => {
        return loadGroups.map((load: LoadGroup) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          return load.load;
        });
      });
    const userLoadingSpaces: any = data.map((loadingSpace: LoadingSpaceToAddInterface) => {
      const { loadGroups, loadGroupsSet, unloadingSpace, ...loadingSpaceData } =
        loadingSpace.newLoadingSpaces.loadingSpace;
      return loadingSpaceData;
    });
    const userLoadConcated = [].concat(...userLoads);
    const ret = {
      userLoads: userLoadConcated,
      userLoadingSpaces,
    };
    return ret;
  }

  public exportProject(separator: 'comma' | 'semicolon', type = 'csv'): Observable<any> {
    if (type === 'csv') {
      return this.projectApiService.getProjectInfo().pipe(
        switchMap((project: ProjectInfo) => {
          return this.createCsv(this.project, project, separator);
        }),
      );
    } else {
      return this.projectApiService.getProjectInfo().pipe(
        switchMap((project: ProjectInfo) => {
          return this.createCsv(this.project, project, separator);
        }),
      );
    }
  }

  private createUnitsRow(unit: string): string[][] {
    const unitsRow: string[][] = [
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      [[...this.replaceUnderScore([CsvColumns.CONFIG_UNITS])]],
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      [[unit]],
    ];
    return unitsRow;
  }

  private createStopsRow(stops: WorkSpace): string[][] {
    const stopsRow: string[][] = [
      [
        ...this.replaceUnderScore([CsvColumns.STOP_NAME]),
        CsvColumns.STREET,
        CsvColumns.CITY,
        ...this.replaceUnderScore([CsvColumns.POSTAL_CODE]),
        CsvColumns.COUNTRY,
      ],
      [
        stops.name.toString(),
        stops.address ? stops.address.toString() : null,
        stops.city ? stops.city.toString() : null,
        stops.postalCode ? stops.postalCode.toString() : null,
        stops.country ? stops.country.toString() : null,
      ],
    ];
    return stopsRow;
  }

  private getAxisValue(cargoSpace: CargoSpace, index: number, key: string): string {
    if (
      !cargoSpace.axis ||
      !Array.isArray(cargoSpace.axis) ||
      !cargoSpace.axis[index] ||
      cargoSpace.axis[index][key] === undefined
    ) {
      return '';
    }
    const value: string = cargoSpace.axis[index][key].toString();
    return value;
  }

  private createLoadingSpaceRow(
    loadingSpace: CargoSpace,
    // isSet = false,
    // isSecond = false,
  ): string[][] {
    let loadingSpaceRow: LoadingSpaceRow = [];
    const loadingSpaceRowVehicle: LoadingSpaceRow = [
      [
        CsvColumns.SPACE_NAME,
        CsvColumns.LENGTH,
        CsvColumns.WIDTH,
        CsvColumns.HEIGHT,
        CsvColumns.LIMIT,
        // CsvColumns.IS_SET,
        // CsvColumns.LOADING_TYPE,
        CsvColumns.LOADING_SIDE,
        CsvColumns.AXIS_DISTANCE_FROM_FRONT,
        CsvColumns.AXIS_2_DISTANCE_FROM_FRONT,
        CsvColumns.AXIS_3_DISTANCE_FROM_FRONT,
        CsvColumns.AXIS_EMPTY_SPACE_LOAD,
        CsvColumns.AXIS_2_EMPTY_SPACE_LOAD,
        CsvColumns.AXIS_3_EMPTY_SPACE_LOAD,
        // CsvColumns.AXIS_LOADING_SPACE_PART,
        // CsvColumns.AXIS_2_LOADING_SPACE_PART,
        CsvColumns.AXIS_MAX_LOAD,
        CsvColumns.AXIS_2_MAX_LOAD,
        CsvColumns.AXIS_3_MAX_LOAD,
        CsvColumns.COOLER_WIDTH,
        CsvColumns.COOLER_HEIGHT,
        CsvColumns.COOLER_LENGTH,
        CsvColumns.COOLER_FLOOR_HEIGHT,
        CsvColumns.WHEEL_WIDTH,
        CsvColumns.WHEEL_HEIGHT,
        CsvColumns.WHEEL_LENGTH,
        CsvColumns.WHEEL_DISTANCE,
        CsvColumns.VARIO_FLOOR_HEIGHT,
        CsvColumns.VARIO_FLOOR_THICKNESS,
        CsvColumns.VARIO_FLOOR_CAPACITY,
      ],
      [
        loadingSpace.name,
        loadingSpace.dimensions.length?.toString(),
        loadingSpace.dimensions.width?.toString(),
        loadingSpace.dimensions.height?.toString(),
        loadingSpace.limit.toString(),
        // isSet ? TrueFalse.TRUE : TrueFalse.FALSE,
        // loadingSpace.type.toString(),
        loadingSpace.trailerLoadingSide,
        this.getAxisValue(loadingSpace, 0, 'distanceFromSpaceFront'),
        this.getAxisValue(loadingSpace, 1, 'distanceFromSpaceFront'),
        this.getAxisValue(loadingSpace, 2, 'distanceFromSpaceFront'),
        this.getAxisValue(loadingSpace, 0, 'emptySpaceLoad'),
        this.getAxisValue(loadingSpace, 1, 'emptySpaceLoad'),
        this.getAxisValue(loadingSpace, 2, 'emptySpaceLoad'),
        this.getAxisValue(loadingSpace, 0, 'maxLoad'),
        this.getAxisValue(loadingSpace, 1, 'maxLoad'),
        this.getAxisValue(loadingSpace, 2, 'maxLoad'),

        // loadingSpace.frontAxi ? loadingSpace.frontAxi.position.z.toString() : null,
        // loadingSpace.rearAxi ? loadingSpace.rearAxi.position.z.toString() : null,
        // loadingSpace.frontAxi ? loadingSpace.frontAxi.emptySpaceLoad.toString() : null,
        // loadingSpace.rearAxi ? loadingSpace.rearAxi.emptySpaceLoad.toString() : null,
        // loadingSpace.frontAxi ? '1' : null,
        // loadingSpace.rearAxi ? '1' : null,
        // loadingSpace.frontAxi ? loadingSpace.frontAxi.maxLoad.toString() : null,
        // loadingSpace.rearAxi ? loadingSpace.rearAxi.maxLoad.toString() : null,
        loadingSpace.extraFeatures.coolerTrailerWidth,
        loadingSpace.extraFeatures.coolerTrailerHeight,
        loadingSpace.extraFeatures.coolerTrailerLength,
        loadingSpace.extraFeatures.coolerTrailerFloorHeight,
        loadingSpace.extraFeatures.wheelTrailerWidth,
        loadingSpace.extraFeatures.wheelTrailerHeight,
        loadingSpace.extraFeatures.wheelTrailerLength,
        loadingSpace.extraFeatures.wheelTrailerDistance,
        loadingSpace.extraFeatures.varioTrailerFloorHeight,
        loadingSpace.extraFeatures.varioTrailerFloorThickness,
        loadingSpace.extraFeatures.varioTrailerFloorCapacity,
      ],
    ];
    const loadingSpaceRowAirContainer: LoadingSpaceRow = [
      [
        CsvColumns.SPACE_NAME,
        CsvColumns.AIR_TYPE,
        CsvColumns.AIR_LENGTH_A,
        CsvColumns.AIR_WIDTH,
        CsvColumns.AIR_BASE_B,
        CsvColumns.AIR_HEIGHT_C,
        CsvColumns.AIR_HEIGHT_D,
        CsvColumns.LIMIT,
        // CsvColumns.LOADING_TYPE,
      ],
      [
        loadingSpace.name,
        loadingSpace.extraFeatures.airType,
        loadingSpace.extraFeatures.airLengthA,
        loadingSpace.extraFeatures.airWidth,
        loadingSpace.extraFeatures.airBaseB,
        loadingSpace.extraFeatures.airHeightC,
        loadingSpace.extraFeatures.airHeightD,
        loadingSpace.limit.toString(),
        // loadingSpace.type.toString(),
      ],
    ];
    const loadingSpaceRowSeaContainer: LoadingSpaceRow = [
      [
        CsvColumns.SPACE_NAME,
        CsvColumns.LENGTH,
        CsvColumns.WIDTH,
        CsvColumns.HEIGHT,
        CsvColumns.LIMIT,
        CsvColumns.LOADING_SIDE,
        //  CsvColumns.IS_SET,
        // CsvColumns.LOADING_TYPE,
        CsvColumns.COOLER_WIDTH,
        CsvColumns.COOLER_HEIGHT,
        CsvColumns.COOLER_LENGTH,
        CsvColumns.COOLER_FLOOR_HEIGHT,
        CsvColumns.GRAVITY_SHOW_CENTER,
        CsvColumns.GRAVITY_LENGTH_TOLERANCE,
        CsvColumns.GRAVITY_WIDTH_TOLERANCE,
        CsvColumns.GRAVITY_CONTAINER_WEIGHT,
        CsvColumns.DOOR_HEIGHT,
        CsvColumns.DOOR_WIDTH,
      ],
      [
        loadingSpace.name,
        loadingSpace.dimensions.length?.toString(),
        loadingSpace.dimensions.width?.toString(),
        loadingSpace.dimensions.height?.toString(),
        loadingSpace.limit.toString(),
        loadingSpace.trailerLoadingSide,
        //  isSet ? TrueFalse.TRUE : TrueFalse.FALSE,
        // loadingSpace.type.toString(),
        loadingSpace.extraFeatures.coolerTrailerWidth,
        loadingSpace.extraFeatures.coolerTrailerHeight,
        loadingSpace.extraFeatures.coolerTrailerLength,
        loadingSpace.extraFeatures.coolerTrailerFloorHeight,
        loadingSpace.extraFeatures.gravityShowCenter ? TrueFalse.TRUE : null,
        loadingSpace.extraFeatures.gravityTrailerLengthTolerance,
        loadingSpace.extraFeatures.gravityTrailerWidthTolerance,
        loadingSpace.extraFeatures.trailerContainerWeight,
        loadingSpace.extraFeatures.trailerDoorHeight,
        loadingSpace.extraFeatures.trailerDoorWidth,
      ],
    ];

    const loadingSpaceRowPallet: LoadingSpaceRow = [
      [
        CsvColumns.SPACE_NAME,
        CsvColumns.LENGTH,
        CsvColumns.WIDTH,
        // CsvColumns.HEIGHT,
        CsvColumns.PALLET_HEIGHT,
        CsvColumns.PALLET_MAX_HEIGHT,
        CsvColumns.LIMIT,
        // CsvColumns.LOADING_TYPE,
        CsvColumns.PALLET_WEIGHT,
        CsvColumns.PALLET_MAX_WEIGHT,
      ],
      [
        loadingSpace.name,
        loadingSpace.dimensions.length?.toString(),
        loadingSpace.dimensions.width?.toString(),
        // loadingSpace.dimensions.height?.toString(),
        loadingSpace.extraFeatures.palletHeight,
        loadingSpace.extraFeatures.palletMaxHeight,
        loadingSpace.limit.toString(),
        // loadingSpace.type.toString(),
        loadingSpace.extraFeatures.palletWeight,
        loadingSpace.extraFeatures.palletMaxWeight,
      ],
    ];

    switch (loadingSpace.type) {
      case LoadingSpaceType.Vehicle:
        loadingSpaceRow = loadingSpaceRowVehicle as unknown as string[][];
        break;
      case LoadingSpaceType.AirContainer:
        loadingSpaceRow = loadingSpaceRowAirContainer as unknown as string[][];
        break;
      case LoadingSpaceType.SeaContainer:
        loadingSpaceRow = loadingSpaceRowSeaContainer as unknown as string[][];
        break;
      case LoadingSpaceType.Pallet:
        loadingSpaceRow = loadingSpaceRowPallet as unknown as string[][];
        break;
      default:
        break;
    }
    const loadingSpaceRowWithoutUnderscore: string[][] = [
      this.replaceUnderScore(loadingSpaceRow[0] as unknown as string[]),
      loadingSpaceRow[1] as unknown as string[],
    ];
    return loadingSpaceRowWithoutUnderscore as unknown as string[][];
  }

  private pushRows(inputData: string[][], target: string[][]): void {
    inputData.forEach((row: string[]): void => {
      target.push(row);
    });
  }

  private replaceUnderScore(data: string[]): string[] {
    return data.map((key: string) => key.replace(/_/g, ' '));
  }

  private createLoadGroupRows(loadGroups: Group[], isStops = false): string[][] {
    if (loadGroups.length === 0) {
      return [];
    }
    let loadGroupRows: string[][] = [
      [
        CsvColumns.LOAD_NAME,
        // CsvColumns.LOAD_TYPE,
        CsvColumns.WIDTH,
        CsvColumns.LENGTH,
        CsvColumns.HEIGHT,
        CsvColumns.DIAMETER,
        CsvColumns.WEIGHT,
        CsvColumns.QUANTITY,
        CsvColumns.STACKING,
        CsvColumns.MAX_LOADS_ON_TOP,
        CsvColumns.MAX_WEIGHT_ON_TOP,
        CsvColumns.ALONG_FLOOR,
        CsvColumns.PCT_SUPPORT,
        CsvColumns.FORMATION,
        CsvColumns.ALLOW_OUT_OF_SPACE,
        // CsvColumns.ALLOW_TO_ROTATE,
        CsvColumns.ROTATE_ON_BASE,
        CsvColumns.ROTATE_FREELY,
        // UNCOMMENT IF NEEDED
        // CsvColumns.CENTER_OF_MASS_X,
        // CsvColumns.CENTER_OF_MASS_Y,
        // CsvColumns.CENTER_OF_MASS_Z,
      ],
    ];
    loadGroupRows = [this.replaceUnderScore(loadGroupRows[0])];
    const quantityIndex: number = loadGroupRows[0].indexOf(CsvColumns.QUANTITY);
    if (isStops) {
      loadGroupRows[0].splice(quantityIndex + 1, 0, CsvColumns.UNLOADING_STOP.replace(/_/g, ' '));
    }
    loadGroups.forEach((loadGroup: Group): void => {
      const isBarrel: boolean = loadGroup.loadType === 1;
      const isPipe: boolean = loadGroup.loadType === 2;
      const changedWidth: string =
        isBarrel || isPipe ? null : loadGroup.dimensions.width.toString();
      const changedLength: string = isBarrel ? null : loadGroup.dimensions.length.toString();
      const changedHeight: string = isPipe ? null : loadGroup.dimensions.height.toString();
      const rotateFreely: TrueFalse = loadGroup.rotateFreely ? TrueFalse.TRUE : null;
      const rotateOnBase: TrueFalse = isBarrel
        ? null
        : loadGroup.allowToRotate === false && loadGroup.rotateOnBase !== true
        ? TrueFalse.FALSE
        : loadGroup.rotateOnBase
        ? TrueFalse.TRUE
        : null;

      const loadGroupRowsBase: string[] = [
        loadGroup.nameTranslated,
        // loadGroup.loadType.toString(),
        changedWidth,
        changedLength,
        changedHeight,
        loadGroup.dimensions.diameter ? loadGroup.dimensions.diameter.toString() : null,
        loadGroup.weight.toString(),
        loadGroup.quantity.toString(),
        loadGroup.stacking ? TrueFalse.TRUE : null,
        loadGroup.stackingOptions.maxLoadsOnTop
          ? loadGroup.stackingOptions.maxLoadsOnTop.toString()
          : null,
        loadGroup.stackingOptions.maxWeightOnTop
          ? loadGroup.stackingOptions.maxWeightOnTop.toString()
          : null,
        loadGroup.stackingOptions.alongFloor ? TrueFalse.TRUE : null,
        loadGroup.stackingOptions.pctSupport
          ? loadGroup.stackingOptions.pctSupport.toString()
          : null,
        loadGroup.stackingOptions.formation ? loadGroup.stackingOptions.formation.toString() : null,
        loadGroup.allowOutOfSpace ? TrueFalse.TRUE : null,
        // loadGroup.allowToRotate === false ? TrueFalse.TRUE : null,
        rotateOnBase,
        rotateFreely,
        // loadGroup.centerOfMass?.x && loadGroup.centerOfMass?.y && loadGroup.centerOfMass?.z
        //   ? loadGroup.centerOfMass.x.toString()
        //   : null,
        // loadGroup.centerOfMass?.x && loadGroup.centerOfMass?.y && loadGroup.centerOfMass?.z
        //   ? loadGroup.centerOfMass.y.toString()
        //   : null,
        // loadGroup.centerOfMass?.x && loadGroup.centerOfMass?.y && loadGroup.centerOfMass?.z
        //   ? loadGroup.centerOfMass.z.toString()
        //   : null,
      ];
      if (isStops) {
        loadGroupRowsBase.splice(quantityIndex + 1, 0, loadGroup.destination.name);
      }
      loadGroupRows.push(loadGroupRowsBase);
    });
    return loadGroupRows;
  }

  private removeStopsLoadsDuplicates(stops: WorkSpace[]): WorkSpace[] {
    const seenIds = new Set<number>();
    return stops.map((stop: WorkSpace): WorkSpace => {
      const uniqueCargoSpaces = stop.cargoSpaces.map((cargoSpace: CargoSpace) => {
        const uniqueGroups: Group[] = cargoSpace.groups.filter((group: Group): boolean => {
          if (seenIds.has(group.id)) {
            return false;
          } else {
            seenIds.add(group.id);
            return true;
          }
        });
        return { ...cargoSpace, groups: uniqueGroups };
      });
      return { ...stop, cargoSpaces: uniqueCargoSpaces };
    });
  }

  private joinGroups(groups: Group[]): Group[] {
    const seenIds = new Set<number>();
    const mergedGroups: Group[] = [];
    groups.forEach((group: Group): void => {
      if (seenIds.has(group.id)) {
        const existingGroup: Group = mergedGroups.find((g: Group): boolean => g.id === group.id);
        existingGroup.quantity += group.quantity;
      } else {
        seenIds.add(group.id);
        mergedGroups.push({ ...group });
      }
    });
    return mergedGroups;
  }

  private mergeSetGroups(cargoSpaces: CargoSpace[]): CargoSpace[] {
    const semiTrailerCargoSpace: CargoSpace = {
      ...cargoSpaces[0],
      groups: [...cargoSpaces[0].groups],
    };
    const trailerCargoSpace: CargoSpace = { ...cargoSpaces[1], groups: [...cargoSpaces[1].groups] };
    const trailerNotTheSameGroups: Group[] = trailerCargoSpace.groups.filter(
      (group: Group): boolean =>
        !semiTrailerCargoSpace.groups.some((g: Group): boolean => g.id === group.id),
    );
    const trailerSameGroups: Group[] = trailerCargoSpace.groups.filter((group: Group): boolean =>
      semiTrailerCargoSpace.groups.some((g: Group): boolean => g.id === group.id),
    );
    const semiTrailerGroups: Group[] = this.joinGroups([
      ...semiTrailerCargoSpace.groups,
      ...trailerSameGroups,
    ]);
    const newTrailerCargoSpace: CargoSpace = {
      ...trailerCargoSpace,
      groups: [...trailerNotTheSameGroups],
    };
    const newSemiTrailerCargoSpace: CargoSpace = {
      ...semiTrailerCargoSpace,
      groups: [...semiTrailerGroups],
    };
    return [newSemiTrailerCargoSpace, newTrailerCargoSpace, ...cargoSpaces.slice(2)];
  }

  private unSetSet(workSpace: WorkSpace): WorkSpace {
    if (workSpace.cargoSpaces.length > 0) {
      const unsetCargoSpace = {
        ...workSpace,
        cargoSpaces: [
          {
            ...workSpace.cargoSpaces[0],
            groups: [...workSpace.cargoSpaces[0].groups],
          },
        ],
      };

      if (workSpace.cargoSpaces[1]) {
        unsetCargoSpace.cargoSpaces[0].groups.push(...workSpace.cargoSpaces[1].groups);
      }

      return unsetCargoSpace;
    } else {
      return workSpace;
    }
  }

  public createCsv(
    project: Project,
    projectInfo: ProjectInfo,
    separator: 'comma' | 'semicolon',
  ): Observable<any> {
    const rows: string[][] = [];
    const units: string = this.project.options.unit;
    if (units === 'mm') {
      project = convertProjectDimensions(project, true);
    }
    const unitsRow: string[][] = this.createUnitsRow(units);
    const isStops: boolean = project.workSpaces.some((workSpace: WorkSpace): boolean => {
      return workSpace.stops.length > 0;
    });
    this.pushRows(unitsRow, rows);
    if (!isStops) {
      project.workSpaces.forEach((workSpace: WorkSpace): void => {
        const noSetWorkspace: WorkSpace = this.unSetSet(workSpace);
        const isSet: boolean = noSetWorkspace.cargoSpaces.length > 1;
        let workCargoSpaces: CargoSpace[] = [...noSetWorkspace.cargoSpaces];
        if (isSet) {
          workCargoSpaces = this.mergeSetGroups(workCargoSpaces);
        }
        workCargoSpaces.forEach((cargoSpace: CargoSpace): void => {
          const loadingSpaceRow: string[][] = this.createLoadingSpaceRow(
            cargoSpace,
            //  isSet
          );
          this.pushRows(loadingSpaceRow, rows);
          const loadGroupRows: string[][] = this.createLoadGroupRows(cargoSpace.groups);
          this.pushRows(loadGroupRows, rows);
        });
      });
    } else {
      project.workSpaces.forEach((workSpace: WorkSpace): void => {
        const noSetWorkspace: WorkSpace = this.unSetSet(workSpace);
        let isLoadingSpaceUsed = false;
        const stopsModified: WorkSpace[] = this.removeStopsLoadsDuplicates(noSetWorkspace.stops);
        stopsModified.forEach((stop: WorkSpace): void => {
          const stopsRow: string[][] = this.createStopsRow(stop);
          this.pushRows(stopsRow, rows);
          const isSet: boolean = noSetWorkspace.cargoSpaces.length > 1;
          if (!isLoadingSpaceUsed) {
            const loadingSpaceRow: string[][] = this.createLoadingSpaceRow(
              stop.cargoSpaces[0],
              //    isSet,
            );
            this.pushRows(loadingSpaceRow, rows);
          }
          const loadGroup: string[][] = this.createLoadGroupRows(
            !isSet
              ? stop.cargoSpaces[0].groups
              : [...stop.cargoSpaces[0].groups, ...stop.cargoSpaces[1].groups],
            true,
          );
          this.pushRows(loadGroup, rows);

          if (isSet && !isLoadingSpaceUsed) {
            const loadingSpaceRow: string[][] = this.createLoadingSpaceRow(
              stop.cargoSpaces[1],
              //       isSet,
            );
            this.pushRows(loadingSpaceRow, rows);
          }
          isLoadingSpaceUsed = true;
        });
      });
    }
    const blob: Blob = new Blob(
      ['\uFEFF', Papa.unparse(rows, { delimiter: separator === 'comma' ? ',' : ';' })],
      {
        type: 'text/csv;charset=utf-8',
      },
    );
    const url: string = window.URL.createObjectURL(blob);
    let fileName: string;

    if (projectInfo && projectInfo.name) {
      fileName = `${projectInfo.name}.csv`;
    } else {
      fileName = `GL${this.datePipe.transform(Date.now(), 'yyMMddHHmm')}.csv`;
    }

    const link: HTMLAnchorElement = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
    return of(true);
  }

  private stopsImportProject(
    convertedData: LoadingSpaceToAddInterface[],
    currentWorkSpaceId: number,
    unit?: string,
  ): void {
    const firstLoadingSpace: LoadingSpaceToAddInterface = convertedData[0];
    const stops: StopInfo[] = firstLoadingSpace.newLoadingSpaces.stops;
    const isSet: boolean = firstLoadingSpace.newLoadingSpaces.loadingSpace.set;
    let loadingSpaceToAdd;
    if (!isSet) {
      const { loadGroups, unloadingSpace, ...loadingSpaceData } =
        firstLoadingSpace.newLoadingSpaces.loadingSpace;
      loadingSpaceToAdd = {
        currentWorkSpaceId: 1,
        newLoadingSpaces: {
          quantity: 1,
          loadingSpace: { ...emptyLoadingSpace, ...loadingSpaceData },
          stops: stops,
        },
      };
    } else {
      const { loadGroups, loadGroupsSet, ...loadingSpaceData } =
        firstLoadingSpace.newLoadingSpaces.loadingSpace;
      loadingSpaceToAdd = {
        currentWorkSpaceId: currentWorkSpaceId,
        newLoadingSpaces: {
          quantity: 1,
          loadingSpace: { ...emptyLoadingSpace, ...loadingSpaceData },
          stops: stops,
        },
      };
    }
    const convertedLoadingSpaceToAdd: LoadingSpaceToAddInterface = loadingSpaceToAdd;
    if (unit === 'mm') {
      convertedLoadingSpaceToAdd.newLoadingSpaces.loadingSpace = convertLoadingSpaceDimensions(
        loadingSpaceToAdd.newLoadingSpaces.loadingSpace,
        false,
      ) as unknown as LoadingSpaceInterface;
    }
    this.projectApiService
      .addLoadingSpace(convertedLoadingSpaceToAdd)
      .pipe(
        switchMap(() => {
          const groupObservables: Observable<LoadGroupToAdd>[] = [];
          convertedData.forEach((loadingSpace: LoadingSpaceToAddInterface): void => {
            let groupToAdd: LoadGroupToAdd;
            loadingSpace.newLoadingSpaces.loadingSpace.loadGroups.forEach(
              (loadGroup: LoadGroup, idx2: number): void => {
                const loadGroupDecorated: LoadGroup[] = this.decorateLoadGroup(
                  loadGroup as unknown as LoadGroup[],
                  loadingSpace.newLoadingSpaces.stops,
                );
                if (loadGroup !== null && parseInt(`${loadGroup.length}`) > 0) {
                  groupToAdd = {
                    currentWorkSpaceId: 1,
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    newLoads: loadGroupDecorated,
                    workSpaceId: 1,
                    currentStopId: idx2 + 1,
                  };
                  let convertedGroupToAdd: LoadGroupToAdd = groupToAdd;
                  if (unit === 'mm') {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    convertedGroupToAdd = convertCsvLoadsDimensions(
                      groupToAdd,
                      false,
                    ) as unknown as LoadGroupToAdd;
                  }
                  groupObservables.push(
                    this.projectApiService.instantAddGroup(convertedGroupToAdd),
                  );
                }
              },
            );
          });
          return from(groupObservables).pipe(
            concatMap((observable: Observable<LoadGroupToAdd>) => observable),
          );
        }),
      )
      .subscribe();
  }

  private nonStopImportProject(
    convertedData: LoadingSpaceToAddInterface[],
    currentWorkSpaceId: number,
    unit?: string,
  ): void {
    let currentId = 0;
    from(convertedData)
      .pipe(
        concatMap((loadingSpace: LoadingSpaceToAddInterface) => {
          currentId++;
          const isSet = loadingSpace.newLoadingSpaces.loadingSpace.set;
          if (!isSet) {
            const { loadGroups, ...loadingSpaceData } = loadingSpace.newLoadingSpaces.loadingSpace;
            const loadingSpaceToAdd = {
              currentWorkSpaceId: currentWorkSpaceId,
              newLoadingSpaces: {
                quantity: 1,
                loadingSpace: { ...emptyLoadingSpace, ...loadingSpaceData },
              },
              workSpaceId: currentWorkSpaceId,
            };
            const convertedLoadingSpaceToAdd = loadingSpaceToAdd;
            if (unit === 'mm') {
              convertedLoadingSpaceToAdd.newLoadingSpaces.loadingSpace =
                convertLoadingSpaceDimensions(
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  loadingSpaceToAdd.newLoadingSpaces.loadingSpace,
                  false,
                ) as unknown as LoadingSpaceInterface;
            }
            const isLoadingSpaceEmpty: boolean = loadingSpaceData.name === undefined;
            return iif(
              () => !isLoadingSpaceEmpty,
              this.projectApiService.addLoadingSpace(convertedLoadingSpaceToAdd),
              of(null),
            ).pipe(
              switchMap(() => {
                const groupObservables: Observable<LoadGroupToAdd>[] = [];
                let groupToAdd: LoadGroupToAdd;
                if (isLoadingSpaceEmpty) {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  groupToAdd = {
                    newLoads: loadGroups,
                  };
                } else {
                  groupToAdd = {
                    currentWorkSpaceId: currentId,
                    newLoads: loadGroups,
                    workSpaceId: currentId,
                  };
                }
                let convertedGroupToAdd: LoadGroupToAdd = groupToAdd;
                if (unit === 'mm') {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  convertedGroupToAdd = convertCsvLoadsDimensions(
                    groupToAdd,
                    false,
                  ) as unknown as LoadGroupToAdd;
                }
                groupObservables.push(this.projectApiService.instantAddGroup(convertedGroupToAdd));
                return from(groupObservables).pipe(concatMap((observable) => observable));
              }),
            );
          } else {
            const { loadGroups, loadGroupsSet, ...loadingSpaceData } =
              loadingSpace.newLoadingSpaces.loadingSpace;
            const loadingSpaceToAdd = {
              currentWorkSpaceId: currentId,
              newLoadingSpaces: {
                quantity: 1,
                loadingSpace: { ...emptyLoadingSpace, ...loadingSpaceData },
              },
              workSpaceId: currentWorkSpaceId,
            };
            const convertedLoadingSpaceToAdd = loadingSpaceToAdd;
            return this.projectApiService.addLoadingSpace(convertedLoadingSpaceToAdd).pipe(
              switchMap(() => {
                const groupObservables: Observable<any>[] = [];
                const groupToAdd = {
                  currentWorkSpaceId: currentId,
                  newLoads: loadGroups as unknown as {
                    load: Partial<Load>;
                    quantity: number;
                  }[],
                  workSpaceId: currentId,
                };
                const setGroupToAdd: GroupAdd = {
                  currentWorkSpaceId: currentId,
                  newLoads: loadGroupsSet as unknown as {
                    load: Partial<Load>;
                    quantity: number;
                  }[],
                  workSpaceId: currentId,
                };
                let convertedGroupToAdd = groupToAdd;
                let convertedSetGroupToAdd = setGroupToAdd;
                if (unit === 'mm') {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  convertedGroupToAdd = convertCsvLoadsDimensions(
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    groupToAdd,
                    false,
                  ) as unknown as LoadGroupToAdd;
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  convertedSetGroupToAdd = convertCsvLoadsDimensions(
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    setGroupToAdd,
                    false,
                  ) as unknown as LoadGroupToAdd;
                }
                groupObservables.push(this.projectApiService.instantAddGroup(convertedGroupToAdd));
                if (setGroupToAdd.newLoads !== undefined) {
                  groupObservables.push(
                    this.projectApiService.instantAddGroup(convertedSetGroupToAdd),
                  );
                }
                return from(groupObservables).pipe(concatMap((observable) => observable));
              }),
            );
          }
        }),
      )
      .subscribe();
  }

  public importProject(data: ProjectData, convertedData: LoadingSpaceToAddInterface[]): void {
    const isStops: boolean = data.isStops;
    const unit: string = data.unit;
    if (unit === 'mm') {
      setLocalStorage('mm', 'unit');
    } else {
      setLocalStorage(unit, 'unit');
    }
    this.projectApiService
      .newProject()
      .pipe(
        concatMap((project: Project) => {
          if (unit !== 'cm' && unit !== 'mm') {
            return this.projectApiService.projectOptionsUpdate({
              currentWorkSpaceId: project.id,
              options: {
                unit: unit,
              },
            });
          } else {
            return of(project);
          }
        }),
      )
      .subscribe((): void => {
        const currentWorkSpaceId: number = this.project.workSpaces[this.currentWorkSpace]
          ? this.project.workSpaces[this.currentWorkSpace].id
          : undefined;
        if (isStops) {
          this.stopsImportProject(convertedData, currentWorkSpaceId, unit);
        } else {
          this.nonStopImportProject(convertedData, currentWorkSpaceId, unit);
        }
      });
  }

  private findIdOfAStop(name: string, stops: StopInfo[]): number {
    return (
      stops.findIndex((stopInfo: StopInfo): boolean => {
        return stopInfo.name === name;
      }) + 1
    );
  }

  private decorateLoadGroup(loadGroup: LoadGroup[], stops: StopInfo[]): LoadGroup[] {
    const modifiedLoadGroup: LoadGroup[] = [];
    loadGroup.forEach((group: LoadGroup): void => {
      const destinationId: number = this.findIdOfAStop(
        group.load.destination.stopId as unknown as string,
        stops,
      );
      const destinationStopName: string = group.load.destination.stopId.toString();
      const originStopName: string = stops[parseInt(destinationId as unknown as string) - 1]
        ?.name as unknown as string;
      group.load.loadingPlaces = {
        origin: {
          name: originStopName,
          id: group.load.stopId,
        },
        destination: {
          name: destinationStopName,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          id: destinationId,
        },
      };
      modifiedLoadGroup.push(group);
    });
    return modifiedLoadGroup;
  }

  private parseCSV(file: File): Observable<string[][]> {
    return new Observable((observable: Subscriber<string[][]>): void => {
      Papa.parse(file, {
        dynamicTyping: true,
        skipEmptyLines: true,
        complete: (results: ParseResult<string[]>): void => {
          try {
            const data: string[][] = results.data as string[][];
            observable.next(data);
            observable.complete();
          } catch (error) {
            observable.error(error);
          }
        },
      });
    });
  }

  private parseXLSX(file: File): Observable<string[][]> {
    return new Observable((observable: Subscriber<string[][]>): void => {
      const reader: FileReader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>): void => {
        const workBook: WorkBook = read(e.target.result);
        const workSheetName: string = workBook.SheetNames[0];
        const workSheet: WorkSheet = workBook.Sheets[workSheetName];
        const data: string[][] = utils.sheet_to_json(workSheet, { header: 1 });
        try {
          observable.next(data);
          observable.complete();
        } catch (error) {
          observable.error(error);
        }
      };
      reader.readAsArrayBuffer(file);
    });
  }
}
